<script setup>
import axios from 'axios'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
    webName: {
        type: String,
        default: ''
    },
    title: {
        type: String,
        default: '新闻动态'
    },
})
const seewhole = ref([
    { id: 1, name: t('index.new2') },
    { id: 2, name: t('index.new3') }
])
const judgeid = ref([
    { id: 2, name: t('index.new5') },
    { id: 1, name: t('index.new4') }
])

// 响应式数据
const newsLists = ref([])
const hoveredItemId = ref(null)
const isLoading = ref(false)
const activeTabId = ref(2)
const hoverTimer = ref(null)
const isTabChanging = ref(false) // 添加标记防止切换期间的hover事件

// 常量定义
const CACHE_EXPIRY = 5 * 60 * 1000
const API_CONFIG = Object.freeze({
    1: { endpoint: '/console/v1/news', limit: 8 },
    2: { endpoint: '/console/v1/announcement', limit: 8 }
})

// 缓存管理
const newsCache = ref({
    1: { data: [], timestamp: 0, isFetching: false },
    2: { data: [], timestamp: 0, isFetching: false }
})

// 计算属性
const activeTabIndex = computed(() =>
    judgeid.value.findIndex(item => item.id === activeTabId.value)
)

// 计算有效的悬停项ID
const validHoveredItemId = computed(() => {
    if (!hoveredItemId.value || newsLists.value.length <= 1) {
        return null
    }
    
    // 检查hoveredItemId是否在当前列表中
    const exists = newsLists.value.some(item => item.id === hoveredItemId.value)
    if (exists && newsLists.value.findIndex(item => item.id === hoveredItemId.value) > 0) {
        return hoveredItemId.value
    }
    
    // 如果没有有效的悬停项，返回列表中的第二个项
    if (newsLists.value.length > 1) {
        return newsLists.value[1].id
    }
    
    return null
})

// 计算悬停项索引
const hoveredItemIndex = computed(() => {
    if (!validHoveredItemId.value) return -1
    return newsLists.value.findIndex(item => item.id === validHoveredItemId.value)
})

// 计算模块位置
const moduleStyle = computed(() => {
    if (isTabChanging.value || !validHoveredItemId.value || hoveredItemIndex.value <= 0) {
        return { 
            top: '0rem',
            opacity: 0,
            transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease'
        }
    }
    
    return {
        top: `${(hoveredItemIndex.value - 1) * 4}rem`,
        opacity: 1,
        transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease'
    }
})

const activeBarStyle = computed(() => {
    const tabWidth = 100 / judgeid.value.length
    const translateX = activeTabIndex.value * 100
    return {
        width: `calc(${tabWidth}% - 8px)`,
        transform: `translateX(calc(${translateX}% + ${activeTabIndex.value * 4.5}px))`,
        top: '5px',
        height: 'calc(100% - 10px)',
        transition: 'transform 0.3s cubic-bezier(0.38, 0, 0.24, 1)'
    }
})

const displayNewsLists = computed(() => {
    return newsLists.value
})

// 生命周期
onMounted(() => {
    fetchNews(activeTabId.value)
})

onUnmounted(() => {
    cleanupTimers()
})

// 方法
const cleanupTimers = () => {
    if (hoverTimer.value) {
        clearTimeout(hoverTimer.value)
        hoverTimer.value = null
    }
}

const handleItemHover = (id) => {
    if (isTabChanging.value) return
    
    cleanupTimers()
    hoverTimer.value = setTimeout(() => {
        // 只设置当前存在的id
        if (newsLists.value.some(item => item.id === id)) {
            hoveredItemId.value = id
        }
    }, 100)
}

const handleTabChange = async (id) => {
    if (activeTabId.value === id || isLoading.value) return

    // 设置切换标记
    isTabChanging.value = true
    cleanupTimers()
    
    // 清除悬停状态
    hoveredItemId.value = null
    activeTabId.value = id

    await fetchNews(id)
    
    // 延迟重置切换标记，确保过渡完成
    setTimeout(() => {
        isTabChanging.value = false
    }, 300)
}

const fetchNews = async (id) => {
    const cache = newsCache.value[id]
    const now = Date.now()

    // 缓存有效且非空时使用缓存
    if (cache.data.length > 0 && now - cache.timestamp < CACHE_EXPIRY) {
        newsLists.value = cache.data
        await nextTick() // 等待DOM更新
        setDefaultHover()
        return
    }

    // 防止重复请求
    if (cache.isFetching) return
    cache.isFetching = true
    isLoading.value = true

    try {
        const config = API_CONFIG[id]
        if (!config) {
            console.warn(`Invalid news type ID: ${id}`)
            return
        }

        const response = await axios.get(config.endpoint, {
            params: { page: 1, limit: config.limit },
            timeout: 5000
        })

        const newData = response.data.data?.list || []

        // 更新缓存
        cache.data = newData
        cache.timestamp = now
        newsLists.value = newData

        await nextTick() // 等待DOM更新
        setDefaultHover()
    } catch (error) {
        handleFetchError(error, cache)
    } finally {
        isLoading.value = false
        cache.isFetching = false
    }
}

const handleFetchError = (error, cache) => {
    console.error('Failed to fetch news:', error)

    // 有缓存数据时使用缓存
    if (cache.data.length > 0) {
        newsLists.value = cache.data
        setDefaultHover()
    } else {
        newsLists.value = []
    }
}

const setDefaultHover = () => {
    if (newsLists.value.length > 1) {
        // 延迟设置，确保DOM已更新
        setTimeout(() => {
            hoveredItemId.value = newsLists.value[1].id
        }, 100)
    }
}

const formatDate = (timestamp, formatType = 1) => {
    if (!timestamp) return '--'

    let date

    if (typeof timestamp === 'string') {
        const numTimestamp = Number(timestamp)
        date = new Date(numTimestamp < 10000000000 ? numTimestamp * 1000 : numTimestamp)
    } else {
        date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp)
    }

    if (isNaN(date.getTime())) return '--'

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return formatType === 2 ? `${year}-${month}-${day}` : `${month}-${day}`
}

const navigateToDetail = (id) => {
    const routes = {
        1: `/news/${id}.html`,
        2: `/announcement/${id}.html`
    }

    const route = routes[activeTabId.value]
    if (route) {
        window.open(route, '_blank')
    }
}

// 鼠标离开容器的处理
const handleMouseLeave = () => {
    cleanupTimers()
    // 不清除hover状态，让模块保持当前位置
}
</script>

<template>
    <div class="Heziyun-journalism Heziyun-padding">
        <div class="Heziyun-defaultWidth">
            <div class="Heziyun-titleSwitching">
                <div class="Heziyun-commodity-title">
                    <div class="Heziyun-headTable" v-intersect="{ class: 'bottom-top', delay: 0 }">NEWS</div>
                    <span v-intersect="{ class: 'bottom-top', delay: 0 }">{{ $t('index.new1') }}</span>
                    <p v-intersect="{ class: 'bottom-top', delay: 100 }"></p>
                </div>
                <div class="t-tabs" v-intersect="{ class: 'opacity', delay: 200 }">
                    <div class="t-tabs__nav-container">
                        <div class="t-tabs__nav">
                            <a-tooltip :content="item.name" v-for="item in judgeid" :key="item.id">
                                <div class="t-tabs__nav-item"
                                    :class="{ 't-tabs__nav-item--active': activeTabId === item.id }"
                                    @click="handleTabChange(item.id)">
                                    {{ item.name }}
                                </div>
                            </a-tooltip>
                        </div>
                        <div class="t-tabs__active-bar" :style="activeBarStyle"></div>
                    </div>
                </div>
            </div>

            <transition name="fade" mode="out-in">
                <!-- 新闻/公告中心 -->
                <div :key="activeTabId" class="Heziyun-newsContent" @mouseleave="handleMouseLeave">
                    <div class="Heziyun-premiere" v-intersect="{ class: 'left-right', delay: 0 }">
                        <template v-if="newsLists[0]">
                            <div class="Heziyun-notShown" @click="navigateToDetail(newsLists[0].id)">
                                <a-image v-if="newsLists[0].img" :src="newsLists[0].img" show-loader :preview="false" />

                                <div v-else class="Heziyun-nothing Heziyun-lightSpeed">
                                    <img src="@/static/img/index/wu.jpg" alt="无图片" />
                                </div>
                            </div>
                            <div class="Heziyun-elaborate">
                                <div class="Heziyun-time">
                                    {{ seewhole[1].name }}：{{ formatDate(newsLists[0].create_time, 2) }}
                                </div>
                                <div class="Heziyun-title">{{ newsLists[0].title }}</div>
                                <a :href="`/new_Smell.html?Class=${activeTabId}`">
                                    {{ seewhole[0].name }}
                                </a>
                            </div>
                        </template>
                    </div>

                    <div class="Heziyun-multiple">
                        <div v-for="(item, index) in displayNewsLists" v-show="index !== 0" :key="item.id"
                            class="Heziyun-item" 
                            :class="{ 'Heziyun-item-hover': validHoveredItemId === item.id }"
                            v-intersect="{ class: 'right-left', delay: index * 30 }"
                            @mouseenter="handleItemHover(item.id)" 
                            @click="navigateToDetail(item.id)">
                            <div class="Heziyun-notShown" :class="{ 'Heziyun-lightSpeed': !item.img }">
                                <img v-if="item.img" :src="item.img" :alt="item.title" loading="lazy" />

                                <div v-else class="Heziyun-nothing">
                                    <img src="@/static/img/index/wu.jpg" alt="无图片" />
                                </div>
                            </div>
                            <span>{{ item.title }}</span>
                            <p>{{ formatDate(item.create_time, 2) }}</p>
                        </div>

                        <!-- 模块位置 -->
                        <div v-if="newsLists.length > 1" 
                            class="Heziyun-module"
                            :style="moduleStyle"
                            v-intersect="{ class: 'right-left', delay: 100 }"></div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<style lang="less" scoped>
/* 样式保持不变，已优化过的部分 */
// 新闻动态
.Heziyun-journalism {
    background-color: #fff;

    .Heziyun-defaultWidth {
        .Heziyun-titleSwitching {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .Heziyun-commodity-title {
                text-align: left;

                .Heziyun-headTable {
                    color: rgb(212 219 227);
                    font-family: "思源黑";
                    font-size: 1rem;
                }

                span {
                    position: relative;
                }
            }

            /* TDesign风格标签切换样式 - 响应式 */
            .t-tabs {
                position: relative;
                overflow: hidden;

                .t-tabs__nav-container {
                    position: relative;
                    background-color: #eee;
                    padding: 4px;
                    font-family: "思源黑";
                    overflow-x: auto;
                    white-space: nowrap;

                    .t-tabs__nav {
                        display: flex;
                        position: relative;
                        z-index: 1;
                        min-width: 100%;

                        .t-tabs__nav-item {
                            flex: 1;
                            text-align: center;
                            padding: 8px 16px;
                            cursor: pointer;
                            color: #666666ad;
                            transition: color 0.2s ease;
                            position: relative;
                            z-index: 2;
                            width: 100px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }

                        .t-tabs__nav-item:hover {
                            color: #333;
                        }

                        .t-tabs__nav-item--active {
                            color: #333;
                            font-weight: 500;
                        }
                    }

                    .t-tabs__active-bar {
                        position: absolute;
                        left: 6px;
                        background-color: #fff;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                        transition: transform 0.3s cubic-bezier(0.38, 0, 0.24, 1);
                    }
                }
            }
        }

        .fade-enter-active,
        .fade-leave-active {
            transition: opacity 0.2s ease;
        }

        .fade-enter,
        .fade-leave-to {
            opacity: 0;
        }

        // 主要内容
        .Heziyun-newsContent {
            display: flex;
            justify-content: space-between;
            gap: 3rem;

            .Heziyun-premiere {
                width: calc(40% - 1.5rem);

                .Heziyun-notShown {
                    width: 100%;
                    height: 18rem;
                    display: block;
                    cursor: pointer;

                    :deep(.arco-image) {
                        width: 100%;
                        height: 100%;
                    }

                    :deep(.arco-image-img) {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }

                    .Heziyun-nothing {
                        width: 100%;
                        height: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background-color: rgb(237 237 237);
                        color: #adadad;
                        font-size: 0.8rem;

                        img {
                            object-fit: cover;
                        }
                    }
                }

                .Heziyun-elaborate {
                    background-color: rgb(249 249 249);
                    padding: 1.5rem;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                    height: calc(100% - 18rem);

                    .Heziyun-time {
                        color: rgb(113, 109, 106);
                        font-size: 0.75rem;
                    }

                    .Heziyun-title {
                        border-bottom: 1.5px solid rgb(219, 227, 237);
                        padding-bottom: 0.7rem;
                        font-size: 1.2rem;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    a {
                        color: #fff;
                        text-align: center;
                        width: 100%;
                        background-color: rgb(26, 92, 255);
                        padding: 0.8rem;

                        &:hover {
                            opacity: 0.55;
                        }
                    }
                }
            }

            .Heziyun-multiple {
                width: calc(60% - 1.5rem);
                display: flex;
                flex-direction: column;
                position: relative;

                .Heziyun-item:nth-child(odd) {
                    border-top: 1.5px solid transparent;
                    border-bottom: 1.5px solid transparent;
                }

                .Heziyun-item {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    height: 4rem;
                    overflow: hidden;
                    position: relative;
                    z-index: 1;

                    .Heziyun-notShown {
                        width: 15%;
                        height: 100%;

                        img {
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                        }

                        .Heziyun-nothing {
                            width: 100%;
                            height: 100%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            background-color: rgb(237 237 237);
                            color: #adadad;
                            font-size: 0.8rem;
                        }
                    }

                    span {
                        color: rgb(174, 174, 176);
                        width: 70%;
                        padding-left: 30px;
                        height: 100%;
                        display: inline-block;
                        border-left: 1.5px solid #fff;
                        position: relative;
                        z-index: 1;
                        line-height: 4rem;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    p {
                        width: 15%;
                        color: rgb(174, 174, 176);
                        text-align: center;
                        height: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: relative;
                        z-index: 1;
                    }
                }

                .Heziyun-item-hover {
                    span {
                        color: #fff;
                    }

                    p {
                        color: #fff;
                    }
                }

                .Heziyun-module {
                    width: 85%;
                    height: 4rem;
                    border: 1.5px solid #fff;
                    position: absolute;
                    right: 0;
                    background-color: rgb(26, 92, 255);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
            }
        }
    }
}

/* 响应式样式保持不变 */
@media (max-width: 768px) {
    .t-tabs__nav-container {
        padding: 2px;
    }

    .t-tabs__nav-item {
        padding: 8px 12px;
        min-width: 70px;
        font-size: 14px;
    }

    .t-tabs__active-bar {
        height: calc(100% - 4px);
    }

    .Heziyun-journalism {
        .Heziyun-defaultWidth {
            .Heziyun-titleSwitching {
                flex-direction: column;

                .Heziyun-commodity-title {
                    .Heziyun-headTable {
                        font-size: 4vw;
                    }

                    p {
                        margin: 3vw 0;
                    }
                }

                .t-tabs {
                    margin-bottom: 5vw;
                }
            }

            .Heziyun-newsContent {
                flex-direction: column;
                gap: 5vw;
                overflow: hidden;

                .Heziyun-premiere {
                    width: 100%;

                    .Heziyun-notShown {
                        height: 50vw;
                    }

                    .Heziyun-elaborate {
                        padding: 4vw;
                        gap: 3vw;
                        height: auto;

                        .Heziyun-time {
                            font-size: 3.5vw;
                        }

                        .Heziyun-title {
                            padding-bottom: 3vw;
                            font-size: 4.5vw;
                        }

                        a {
                            font-size: 4vw;
                            padding: 2.5vw;
                        }
                    }
                }

                .Heziyun-multiple {
                    width: 100%;

                    .Heziyun-item {
                        height: 12vw;

                        span {
                            padding-left: 5vw;
                            line-height: 11vw;
                            font-size: 3.5vw;
                        }

                        p {
                            width: 30%;
                            font-size: 3vw;
                        }
                    }

                    .Heziyun-module {
                        height: 12vw;
                        width: 87%;
                    }
                }
            }
        }
    }
}
</style>